import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { productCartMeta } from '@/lib/catalog';
import { categoriaLabel } from '@/lib/categories';
import { MOCK_KITS, MOCK_PRODUCTOS } from '@/data/mockCatalog';
import { useCartStore } from '@/stores/cartStore';
import { CatalogBanner } from '@/components/layout/CatalogBanner';
import { KitListCard } from '@/components/catalog/KitCard';
import { ItemDetailLayout, KitIncludesSection } from '@/components/detail/ItemDetailLayout';
import type { Kit, Producto } from '@/types';

export function ProductDetailPage() {
  const { id } = useParams();
  const [product, setProduct] = useState<Producto | null>(null);
  const addItem = useCartStore((s) => s.addItem);

  useEffect(() => {
    if (!id) return;
    api
      .producto(id)
      .then(setProduct)
      .catch(() => {
        const n = Number(id);
        setProduct(
          Number.isFinite(n)
            ? (MOCK_PRODUCTOS.find((p) => p.id_pto === n) ?? null)
            : null,
        );
      });
  }, [id]);

  if (!product) {
    return (
      <div className="container-app py-20">
        <div className="mx-auto h-8 w-48 animate-pulse rounded bg-surface-200" />
      </div>
    );
  }

  const precio = product.precio_oferta ?? product.precio_venta;
  const catLabel = categoriaLabel(product.categoria);
  const cartMeta = productCartMeta(product);

  const specs = [
    { label: 'Modelo', value: product.modelo_pto },
    product.marca ? { label: 'Marca', value: product.marca } : null,
    product.sku ? { label: 'SKU', value: product.sku } : null,
    product.categoria
      ? {
          label: 'Categoría',
          value: catLabel,
          href: `/catalogo?categoria=${product.categoria}`,
        }
      : null,
    product.clave_prod_serv ? { label: 'Clave SAT', value: product.clave_prod_serv } : null,
  ].filter(Boolean) as { label: string; value: string; href?: string }[];

  return (
    <ItemDetailLayout
      tipo="producto"
      breadcrumbs={[
        { label: 'Catálogo', to: '/catalogo' },
        ...(product.categoria
          ? [{ label: catLabel, to: `/catalogo?categoria=${product.categoria}` }]
          : []),
        { label: product.nombre_pto },
      ]}
      badge={catLabel}
      title={product.nombre_pto}
      subtitle={`Modelo ${product.modelo_pto}`}
      imagen={product.imagen}
      imagenAlt={product.nombre_pto}
      imagenCategoria={product.categoria}
      marca={product.marca}
      precio={precio}
      precioLista={product.precio_oferta ? product.precio_venta : null}
      existencias={product.existencias}
      descripcion={product.descripcion_web ?? product.descripcion_pto ?? ''}
      specs={specs}
      maxCantidad={Math.max(1, product.existencias)}
      onAgregar={(cantidad) =>
        addItem({
          tipo: 'producto',
          refId: cartMeta.refId,
          nombre: product.nombre_pto,
          imagen: product.imagen,
          precio,
          cantidad,
          origen: cartMeta.origen,
          idExterno: cartMeta.idExterno,
          skuExterno: cartMeta.skuExterno,
        })
      }
    />
  );
}

export function KitsPage() {
  const [kits, setKits] = useState<Kit[]>([]);

  useEffect(() => {
    api.kits().then(setKits).catch(() => setKits(MOCK_KITS));
  }, []);

  return (
    <>
      <CatalogBanner
        title="Kits para instalación"
        subtitle="Sistemas CCTV e intrusión con componentes ya definidos. Abre cada kit, revisa qué incluye y agrégalo al carrito con un precio total."
        breadcrumb={[{ label: 'Kits' }]}
      />
      <div className="page-section">
        <div className="container-app">
          {kits.length === 0 ? (
            <p className="text-center text-surface-600">No hay kits disponibles por el momento.</p>
          ) : (
            <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
              {kits.map((k) => (
                <KitListCard key={k.id_kit} kit={k} />
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export function KitDetailPage() {
  const { slug } = useParams();
  const [kit, setKit] = useState<Kit | null>(null);
  const addItem = useCartStore((s) => s.addItem);

  useEffect(() => {
    if (!slug) return;
    api
      .kit(slug)
      .then(setKit)
      .catch(() => setKit(MOCK_KITS.find((k) => k.slug === slug) ?? null));
  }, [slug]);

  if (!kit) {
    return (
      <div className="container-app py-20">
        <div className="h-8 w-48 animate-pulse rounded bg-surface-200" />
      </div>
    );
  }

  const precio = kit.precio_oferta ?? kit.precio;
  const existencias = kit.existencias_disponibles ?? 0;
  const totalPiezas = kit.productos.reduce((n, p) => n + p.cantidad, 0);
  const linea = kit.tipo_venta_producto ?? 'Sistema completo';

  const specs = [
    { label: 'Línea', value: linea },
    { label: 'Referencias', value: `${kit.productos.length} productos distintos` },
    { label: 'Piezas totales', value: String(totalPiezas) },
    kit.slug ? { label: 'Código kit', value: kit.slug } : null,
  ].filter(Boolean) as { label: string; value: string }[];

  return (
    <ItemDetailLayout
      tipo="kit"
      breadcrumbs={[
        { label: 'Kits', to: '/kits' },
        { label: kit.nombre },
      ]}
      badge={`Kit · ${totalPiezas} piezas`}
      title={kit.nombre}
      subtitle={linea}
      imagen={kit.imagen}
      imagenAlt={kit.nombre}
      imagenCategoria="kit"
      precio={precio}
      precioLista={kit.precio_oferta ? kit.precio : null}
      existencias={existencias}
      existenciaTexto={
        existencias <= 0
          ? 'Sin kits completos disponibles por ahora'
          : existencias === 1
            ? '1 kit disponible para surtir'
            : `${existencias} kits disponibles para surtir`
      }
      descripcion={kit.descripcion_web ?? kit.descripcion}
      specs={specs}
      maxCantidad={Math.max(1, existencias)}
      onAgregar={(cantidad) =>
        addItem({
          tipo: 'kit',
          refId: kit.id_kit,
          nombre: kit.nombre,
          imagen: kit.imagen,
          precio,
          cantidad,
          esKit: true,
        })
      }
    >
      <KitIncludesSection productos={kit.productos} />
    </ItemDetailLayout>
  );
}
